import { CustomError, MiniSpinner, reportErrorToServer, UploadFilesDto, useUploadFiles } from '@applyonline/shared'
import { EApplicantType, EFinancingProgram } from '@if/core/types'
import { ChangeEvent, Dispatch } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { getRecaptchaToken } from '../services/recaptcha'
import { Constants } from '../types'

interface Props {
  applicantType: EApplicantType
  typeId: number
  subKey: string | null
  creditApplicationId: string
  disabled?: boolean
  id: string
  setError: Dispatch<CustomError>
  setUploadError: Dispatch<Error | null>
}

const UploadFileButton = ({
  applicantType,
  creditApplicationId,
  typeId,
  subKey,
  disabled = false,
  id,
  setError,
  setUploadError,
}: Props) => {
  const { t } = useTranslation()
  const MAX_FILE_SIZE = 20971520 // 20MB
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [uploadFile, isUploading, reset] = useUploadFiles(EFinancingProgram.Personal)

  const icon = isUploading ? <MiniSpinner color="white" /> : <i className="fa-solid fa-arrow-up" />

  const onChangeFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    if (!files || files.length === 0) return

    setUploadError(null)

    try {
      const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_document')

      const dto: UploadFilesDto = {
        creditApplicationId,
        applicantType,
        typeId,
        subKey,
        files: Array.from(files),
        recaptcha: recaptchaToken,
      }

      const hasError = dto.files.some((file) => {
        if (file.size > MAX_FILE_SIZE) {
          const customError: CustomError = new Error(t(`common.errors.fileSizeTooBig`))
          customError.id = id
          setError(customError)
          return true
        }

        const fileNameSplit = file.name.split('.').pop() ?? ''
        const supportedExtension = Constants.SupportedDocumentTypes.includes(fileNameSplit)

        if (!supportedExtension) {
          const customError: CustomError = new Error(t(`common.errors.fileTypeNotSupported`))
          customError.id = id
          setError(customError)
          return true
        }

        return false
      })

      if (hasError) {
        return
      }

      await uploadFile(dto)
      reset()
    } catch (error) {
      reportErrorToServer(error as Error)
      setUploadError(error as Error)
    }
  }

  return (
    <label className="icon-btn" htmlFor={id}>
      <span style={{ color: 'white', padding: '6px' }}>{icon}</span>
      <input
        id={id}
        style={{ display: 'none' }}
        type="file"
        accept={Constants.SupportedDocumentTypes}
        onChange={onChangeFileSelected}
        disabled={isUploading || disabled}
        multiple
      />
    </label>
  )
}

export default UploadFileButton
